import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_CONFIG } from '../../apis/api-config';
import { getStorage } from '../../hooks/useAuth';

export interface User {
    id: string;
    name: string;
    profileImageUrl?: string;
    organizationUserRole: number;
    language: string;
    activeOrganization: ActiveOrganization;
    usersOrganizations: Organization[];
}

interface ActiveOrganization {
    id: number;
    name: string;
}

interface Organization {
    lastVisited: string;
    role: number;
    id: number;
    name: string;
}

interface AuthState {
    currentUser: User | null;
    loading: boolean;
    error: string | null;
}

const initialState: AuthState = {
    currentUser: null,
    loading: false,
    error: null,
};

// Async thunk to fetch current user
export const fetchCurrentUser = createAsyncThunk(
    'auth/fetchCurrentUser',
    async (_, { rejectWithValue }) => {
        try {
            const token = getStorage().getItem('access_token');

            const response = await axios.get(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CURRENT_USER}`,
                {
                    timeout: API_CONFIG.TIMEOUT,
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || 'Failed to fetch user');
        }
    }
);

const currentUserSlice = createSlice({
    name: 'currentUser',
    initialState,
    reducers: {
        clearUser: (state) => {
            state.currentUser = null;
        },
        updateCurrentUser: (state, action: PayloadAction<User>) => {
            state.currentUser = action.payload;
        },
        updateCurrentUserImage: (state, action: PayloadAction<string>) => {
            if (state.currentUser) {
                state.currentUser.profileImageUrl = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCurrentUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCurrentUser.fulfilled, (state, action: PayloadAction<User>) => {
                state.loading = false;
                state.currentUser = action.payload;
            })
            .addCase(fetchCurrentUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearUser } = currentUserSlice.actions;

export default currentUserSlice.reducer;